import { useEffect, useState } from "react";
import {
  Paper,
  Typography,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Pagination,
  Stack,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useOutletContext, useParams } from "react-router";

import useGetExternalMediaLogs from "../../hooks/useGetExternalMediaLogs";
import useGetTrialForPatient from "../../hooks/useGetTrialForPatient";
import useGetExternalMediaLogsFile from "../../hooks/useGetExternalMediaLogsFile";

const ExternalMediaLogs = ({ mediaType }) => {
  const { _id_: patientId } = useParams();
  const label = mediaType === "photo" ? "Photo" : "Video";

  const { trialFilter, setActive, setBreadcrumbTitle } = useOutletContext();
  setActive(mediaType === "photo" ? "externalPhotoLogs" : "externalVideoLogs");
  setBreadcrumbTitle(`External ${label} Logs`);

  const [page, setPage] = useState(0);
  const [trialId, setTrialId] = useState(0);
  const [fullScreenOpen, setFullScreenOpen] = useState(false);

  const { data: trialData } = useGetTrialForPatient({
    patientId,
    trialId: trialFilter,
    enabled: !!trialFilter,
  });

  useEffect(() => {
    if (trialData?.trial?.id) {
      setTrialId(trialData.trial.id);
    }
  }, [trialData]);

  const { data, isLoading, isFetching } = useGetExternalMediaLogs({
    page: page + 1,
    patientId,
    trialId,
    mediaType: label,
    enabled: !!trialId && !!patientId,
  });

  const { refetch: fetchExternalMediaLogsFile, isFetching: isExporting } =
    useGetExternalMediaLogsFile({
      patientId,
      trialId,
      mediaType: label,
      enabled: false,
    });

  const handleExportCSV = async () => {
    try {
      const res = await fetchExternalMediaLogsFile();
      const fileUrl = res?.data?.file_url;

      if (fileUrl) {
        window.open(fileUrl, "_blank");
      }
    } catch (err) {
      console.error("Failed to export external media logs", err);
    }
  };

  const headerCellSx = {
    backgroundColor: (theme) => theme.palette.grey[100],
    fontWeight: "bold",
    textTransform: "uppercase",
    fontSize: "0.85rem",
    color: "#424242",
    whiteSpace: "nowrap",
  };

  const TableContent = (
    <>
      <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 520 }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={headerCellSx}>SiteID - SubjectsID</TableCell>
              <TableCell sx={headerCellSx}>Trial Name</TableCell>
              <TableCell sx={headerCellSx}>Visit Name</TableCell>
              <TableCell sx={headerCellSx}>Subject Email</TableCell>
              <TableCell sx={headerCellSx}>Subject First Name</TableCell>
              <TableCell sx={headerCellSx}>Subject Last Name</TableCell>
              <TableCell sx={headerCellSx}>Updater Email</TableCell>
              <TableCell sx={headerCellSx}>Updater First Name</TableCell>
              <TableCell sx={headerCellSx}>Updater Last Name</TableCell>
              <TableCell sx={headerCellSx}>Uploaded At</TableCell>
              <TableCell sx={headerCellSx}>Media Name</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {(isLoading || isFetching) && (
              <TableRow>
                <TableCell colSpan={11} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={28} />
                </TableCell>
              </TableRow>
            )}

            {!isLoading &&
              !isFetching &&
              data?.results?.length > 0 &&
              data.results.map((row) => (
                <TableRow key={row.id} hover>
                  <TableCell>{row.site_participant_id ?? "-"}</TableCell>
                  <TableCell>{row.trial_name ?? "-"}</TableCell>
                  <TableCell>{row.visit_name ?? "-"}</TableCell>
                  <TableCell>{row.subject_email ?? "-"}</TableCell>
                  <TableCell>{row.subject_first_name ?? "-"}</TableCell>
                  <TableCell>{row.subject_last_name ?? "-"}</TableCell>
                  <TableCell>{row.uploader_email ?? "-"}</TableCell>
                  <TableCell>{row.uploader_first_name ?? "-"}</TableCell>
                  <TableCell>{row.uploader_last_name ?? "-"}</TableCell>
                  <TableCell>
                    {row.uploaded_at
                      ? `${new Date(row.uploaded_at).toLocaleDateString()} ${new Date(
                          row.uploaded_at
                        ).toLocaleTimeString()}`
                      : "-"}
                  </TableCell>
                  <TableCell>{row.media_real_name ?? "-"}</TableCell>
                </TableRow>
              ))}

            {!isLoading && !isFetching && (!data?.results || data.results.length === 0) && (
              <TableRow>
                <TableCell colSpan={11} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">No media logs found</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Stack direction="row" justifyContent="center" sx={{ pt: 2 }}>
        <Pagination
          color="primary"
          page={page + 1}
          count={Math.ceil((data?.count || 0) / 20)}
          onChange={(_, newPage) => setPage(newPage - 1)}
        />
      </Stack>
    </>
  );

  return (
    <Paper elevation={1} sx={{ p: 2 }}>
      {/* Header */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">External {label} Logs</Typography>

        <Stack direction="row" spacing={1}>
          <Button variant="contained" size="small" onClick={handleExportCSV} disabled={isExporting}>
            {isExporting ? "Exporting..." : "Export CSV"}
          </Button>

          <Button variant="outlined" size="small" onClick={() => setFullScreenOpen(true)}>
            Full Screen View
          </Button>
        </Stack>
      </Stack>

      {/* Normal View */}
      {TableContent}

      {/* Full Screen Dialog */}
      <Dialog open={fullScreenOpen} onClose={() => setFullScreenOpen(false)} fullScreen>
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h6">External {label} Logs – Full Screen</Typography>
          <IconButton onClick={() => setFullScreenOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 2 }}>{TableContent}</DialogContent>
      </Dialog>
    </Paper>
  );
};

export default ExternalMediaLogs;
